<template>
  <div :class="[bem.b(), bem.m(type)]" :style="rootStyle">
    <template v-if="renderList.length">
      <!-- 奖台区：仅奖台样式展示前三名 -->
      <div
        v-if="type === 'podium' && podiumList.length"
        :class="bem.e('podium')"
      >
        <div
          v-for="item in podiumList"
          :key="item.rank"
          :class="[bem.e('podium-item'), bem.is(`rank-${item.rank}`)]"
          @click="(e) => handleClick(e, item)"
        >
          <div :class="bem.e('podium-avatar')">
            <ll-avatar
              v-if="showAvatar && item.avatar"
              v-bind="item.avatar"
              :class="[bem.e('avatar'), bem.is(`top-${item.rank}`)]"
            />
            <span :class="[bem.e('medal'), bem.is(`top-${item.rank}`)]">
              <slot name="avatar" v-bind="item">{{ item.rank }}</slot>
            </span>
          </div>
          <div :class="bem.e('podium-label')" :title="item.label">
            <slot name="label" v-bind="item">{{ item.label }}</slot>
          </div>
          <div :class="bem.e('podium-value')">
            <slot name="value" v-bind="item">{{ item.valueText }}</slot>
          </div>
        </div>
      </div>

      <!-- 列表区 -->
      <div :class="bem.e('list')">
        <div
          v-for="item in listList"
          :key="item.rank"
          :class="[bem.e('item'), bem.is(`rank-${item.rank}`)]"
          @click="(e) => handleClick(e, item)"
        >
          <slot name="item" v-bind="item">
            <!-- 排名 -->
            <div :class="[bem.e('rank'), bem.is(rankLevel(item.rank))]">
              <span :class="bem.e('rank-text')">{{ item.rank }}</span>
            </div>

            <!-- 头像 -->
            <ll-avatar
              v-if="showAvatar && item.avatar"
              v-bind="item.avatar"
              :class="bem.e('avatar')"
            />

            <!-- 内容 -->
            <div :class="bem.e('content')">
              <div :class="bem.e('head')">
                <div :class="bem.e('label')" :title="item.label">
                  <slot name="label" v-bind="item">{{ item.label }}</slot>
                </div>
                <div :class="bem.e('value')">
                  <slot name="value" v-bind="item">{{ item.valueText }}</slot>
                </div>
              </div>

              <div v-if="item.description" :class="bem.e('description')">
                {{ item.description }}
              </div>

              <div v-if="showBar" :class="bem.e('bar')">
                <div :class="bem.e('bar-inner')" :style="barStyle(item)"></div>
              </div>
            </div>
          </slot>
        </div>
      </div>
    </template>

    <div v-else :class="bem.e('empty')">
      <slot name="empty">
        <ll-empty />
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { createNamespace } from '@ll-plus/utils'
import { computed } from 'vue'
import {
  RANKING_LIST_MAX_LIMIT,
  rankingListProps,
  type RankingListItem,
  type RankingListRenderItem,
  RankingListEmits,
} from './ranking-list'

defineOptions({ name: 'LlRankingList' })

const bem = createNamespace('ranking-list')

const props = defineProps(rankingListProps)

const emits = defineEmits(RankingListEmits)

// 字段映射后的取值
const getLabel = (item: RankingListItem) =>
  item?.[props.fieldNames.label || 'label']
const getValue = (item: RankingListItem) =>
  item?.[props.fieldNames.value || 'value']
const getDescription = (item: RankingListItem) =>
  item?.[props.fieldNames.description || 'description']
const getAvatar = (item: RankingListItem) =>
  item?.[props.fieldNames.avatar || 'avatar']

// 数值格式化
const formatValue = (value: number, item: RankingListItem) => {
  if (typeof props.valueFormatter === 'function') {
    return props.valueFormatter(value, item)
  }
  if (typeof value !== 'number' || Number.isNaN(value)) return '-'
  return props.thousandSeparator ? value.toLocaleString('en-US') : String(value)
}

// 展示数量（最多 500 条）
const safeLimit = computed(() => {
  const limit = Number(props.limit)
  if (!Number.isFinite(limit) || limit <= 0) return RANKING_LIST_MAX_LIMIT
  return Math.min(Math.floor(limit), RANKING_LIST_MAX_LIMIT)
})

// 排序 + 截断 + 计算占比
const renderList = computed<RankingListRenderItem[]>(() => {
  const list = (props.data || []).map((raw) => {
    const value = Number(getValue(raw)) || 0
    return {
      raw,
      label: getLabel(raw) ?? '',
      value,
      description: getDescription(raw) ?? '',
      avatar: getAvatar(raw),
    }
  })

  list.sort((a, b) =>
    props.order === 'asc' ? a.value - b.value : b.value - a.value,
  )

  const sliced = list.slice(0, safeLimit.value)
  const maxValue = sliced.reduce((max, item) => Math.max(max, item.value), 0)

  return sliced.map((item, index) => ({
    rank: index + 1,
    label: item.label,
    value: item.value,
    valueText: formatValue(item.value, item.raw),
    description: item.description,
    avatar: item.avatar,
    percent: maxValue > 0 ? (item.value / maxValue) * 100 : 0,
    raw: item.raw,
  }))
})

// 奖台前三名，按 2-1-3 排列（第 1 名居中）
const podiumList = computed<RankingListRenderItem[]>(() => {
  const top = renderList.value.slice(0, 3)
  return [top[1], top[0], top[2]].filter(Boolean)
})

// 列表区数据：奖台样式排除前三名，列表样式展示全部
const listList = computed<RankingListRenderItem[]>(() =>
  props.type === 'podium' ? renderList.value.slice(3) : renderList.value,
)

// 排名等级，仅奖台样式下前三名高亮（列表区一般为第 4 名起，无高亮）
const rankLevel = (rank: number) => {
  if (props.type !== 'podium') return ''
  return rank <= 3 ? `top-${rank}` : ''
}

const rootStyle = computed(() => {
  const style: Record<string, string> = {}
  if (props.background) style.background = props.background
  if (props.color) style.color = props.color
  return style
})

const barStyle = (item: RankingListRenderItem) => {
  const style: Record<string, string> = {
    width: `${item.percent}%`,
  }
  if (props.barColor) style.background = props.barColor
  return style
}

const handleClick = (e: MouseEvent, item: RankingListRenderItem) => {
  emits('item-click', e, item)
}
</script>
